import {
  Directive,
  ElementRef,
  HostListener,
  Renderer2,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  inject,
  AfterViewInit,
  OnInit,
  booleanAttribute,
  numberAttribute,
  ChangeDetectorRef,
  NgZone,
  DestroyRef,
} from '@angular/core';

import { MatPaginator } from '@angular/material/paginator';
import { MatExpansionPanel } from '@angular/material/expansion';
import { environment } from '../../../../environments/environment';
import { fromEvent, filter, combineLatest, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KEYBOARD_EVENT_CODES } from '../../utility/constants';
import { PaginatorArrowWindowRegistryService } from '../../services/paginator-arrow-window-registry.service';
import { isActiveElementInsideCdkOverlay } from './paginator-keyboard-guards';

const DOCUMENT_KEYDOWN_CAPTURE_NON_PASSIVE: AddEventListenerOptions = {
  capture: true,
  passive: false,
};

function removeDocumentKeydownCaptureListener(listener: EventListener): void {
  document.removeEventListener(
    'keydown',
    listener,
    DOCUMENT_KEYDOWN_CAPTURE_NON_PASSIVE
  );
}

/** Registers `document` keydown; removal must use `removeDocumentKeydownCaptureListener` with the same listener reference. */
function addDocumentKeydownCaptureListener(listener: EventListener): void {
  document.addEventListener(
    'keydown',
    listener,
    DOCUMENT_KEYDOWN_CAPTURE_NON_PASSIVE
  );
}

/** Same options as document capture; use on `window` so handlers run before the event reaches any element listeners (e.g. mat-select). */
const WINDOW_KEYDOWN_CAPTURE_NON_PASSIVE: AddEventListenerOptions = {
  capture: true,
  passive: false,
};

function removeWindowKeydownCaptureListener(listener: EventListener): void {
  window.removeEventListener(
    'keydown',
    listener,
    WINDOW_KEYDOWN_CAPTURE_NON_PASSIVE
  );
}

function addWindowKeydownCaptureListener(listener: EventListener): void {
  window.addEventListener(
    'keydown',
    listener,
    WINDOW_KEYDOWN_CAPTURE_NON_PASSIVE
  );
}

/**
 * Binds host root from the ref and registers capture document keydown (see `addDocumentKeydownCaptureListener`).
 * `shortcutKind` is for diagnostics / disambiguation only.
 */
function mountHostElementWithDocumentCaptureKeydown(
  hostRef: ElementRef<HTMLElement>,
  documentKeydownListener: EventListener,
  shortcutKind: string
): HTMLElement {
  void shortcutKind;
  addDocumentKeydownCaptureListener(documentKeydownListener);
  return hostRef.nativeElement;
}

const SUMMARY_TABLE_DATA_ROW_SELECTOR = 'mat-row, tr[mat-row]';

function querySummaryTableDataRows(host: HTMLElement): HTMLElement[] {
  return Array.from(host.querySelectorAll<HTMLElement>(SUMMARY_TABLE_DATA_ROW_SELECTOR));
}

/** Shift+Alt row toggle: digits 1–9 from main row or numpad (`event.code`). */
function parseOneBasedRowIndexFromDigitCode(code: string): number | null {
  const topRow = /^Digit([1-9])$/.exec(code);
  if (topRow) {
    return Number(topRow[1]);
  }
  const numpad = /^Numpad([1-9])$/.exec(code);
  if (numpad) {
    return Number(numpad[1]);
  }
  return null;
}

/** Ctrl+Alt hyperlink: any digit 0–9 from physical key or `event.key` fallback. */
function parseDigitCharForHyperlink(event: KeyboardEvent): string | null {
  const d = /^Digit(\d)$/.exec(event.code);
  if (d) {
    return d[1];
  }
  const n = /^Numpad(\d)$/.exec(event.code);
  if (n) {
    return n[1];
  }
  const k = event.key;
  if (k.length === 1 && k >= '0' && k <= '9') {
    return k;
  }
  return null;
}

/** First interactive control in a section after Alt+letter jump (scroll + focus). */
const SECTION_FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusFirstFocusableIn(container: HTMLElement): void {
  const el = container.querySelector<HTMLElement>(SECTION_FOCUSABLE_SELECTOR);
  el?.focus({ preventScroll: true });
}

/** Esc-to-first-field: Alt+/ digit `1`, else first textual control; avoids datepicker/button ahead of inputs in DOM. */
function resolveFirstShortcutRegionField(container: HTMLElement): HTMLElement | null {
  const shortcutOneSelectors = [
    'input[data-shortcut-number="1"]:not([disabled]):not([type="hidden"])',
    'textarea[data-shortcut-number="1"]:not([disabled])',
    'select[data-shortcut-number="1"]:not([disabled])',
  ].join(',');
  const byShortcutOne =
    container.querySelector<HTMLElement>(shortcutOneSelectors);
  if (byShortcutOne) {
    return byShortcutOne;
  }
  const firstTextLike = container.querySelector<HTMLElement>(
    'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])'
  );
  return (
    firstTextLike ??
    container.querySelector<HTMLElement>(SECTION_FOCUSABLE_SELECTOR)
  );
}

const MAT_CHECKBOX_INPUT_SELECTOR =
  'mat-checkbox input.mdc-checkbox__native-control, mat-checkbox input[type="checkbox"], mat-checkbox input';

@Directive({
  selector: '[appFocusOnEscape]',
})
// Escape key brings focus to first input
export class FocusOnEscapeDirective {
  constructor(private readonly elementRef: ElementRef<HTMLInputElement>) {}

  @HostListener('document:keydown.escape')
  goToFirstInput() {
    this.elementRef.nativeElement.focus();
  }
}

// brings focus on the header "select all" checkbox (Shift+Alt+C)
@Directive({
  selector: '[appTableFocusCheckboxShortcut]',
})
export class TableFocusCheckboxShortcutDirective {
  constructor(private readonly el: ElementRef<HTMLElement>) {}

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const isShiftAltC =
      !event.repeat &&
      event.shiftKey &&
      event.altKey &&
      ((event.key.length === 1 && event.key.toLowerCase() === 'c') ||
        event.code === KEYBOARD_EVENT_CODES.KeyC);
    if (!isShiftAltC) {
      return;
    }
    const checkboxElement = this.findHeaderSelectAllInput();
    if (!checkboxElement) {
      return;
    }
    event.preventDefault();
    if (!checkboxElement.hasAttribute('tabindex')) {
      checkboxElement.setAttribute('tabindex', '0');
    }
    checkboxElement.focus();
    checkboxElement.click();
  }

  /** Header row only — avoids matching the first body-row checkbox. */
  private findHeaderSelectAllInput(): HTMLInputElement | null {
    const host = this.el.nativeElement;
    const headerCheckbox = host.querySelector(
      'mat-header-row mat-checkbox input[type="checkbox"], mat-header-cell mat-checkbox input[type="checkbox"]'
    ) as HTMLInputElement | null;
    if (headerCheckbox) {
      return headerCheckbox;
    }
    return host.querySelector(
      'mat-checkbox input[type="checkbox"]'
    ) as HTMLInputElement | null;
  }
}

// Shift+Alt+Digit1–9: toggle row checkbox (1-based visible data rows on current page).
@Directive({
  selector: '[appfocusonindividualTabCheckbox]',
})
export class TabFocusOnIndividualCheckboxDirective implements OnDestroy {
  private readonly host: HTMLElement;
  private readonly onDocumentKeyDown = (event: Event): void => {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }
    this.handleDocumentKeyDown(event);
  };

  constructor(el: ElementRef<HTMLElement>) {
    this.host = mountHostElementWithDocumentCaptureKeydown(
      el,
      this.onDocumentKeyDown,
      'shift-alt-row-checkbox'
    );
  }

  ngOnDestroy(): void {
    removeDocumentKeydownCaptureListener(this.onDocumentKeyDown);
  }

  private handleDocumentKeyDown(event: KeyboardEvent): void {
    if (event.repeat) {
      return;
    }
    const isShiftAltDigitChord =
      event.shiftKey &&
      event.altKey &&
      !event.ctrlKey &&
      !event.metaKey;
    if (!isShiftAltDigitChord) {
      return;
    }
    const rowIndex = parseOneBasedRowIndexFromDigitCode(event.code);
    if (rowIndex === null) {
      return;
    }
    event.preventDefault();
    this.toggleCheckboxAtDataRow(rowIndex);
  }

  private toggleCheckboxAtDataRow(oneBasedIndex: number): void {
    const rows = querySummaryTableDataRows(this.host);
    const row = rows[oneBasedIndex - 1];
    if (!row) {
      return;
    }
    const input = row.querySelector<HTMLInputElement>(MAT_CHECKBOX_INPUT_SELECTOR);
    if (!input) {
      return;
    }
    input.click();
  }
}

// Ctrl+Alt+P: focus rows-per-page mat-select and open panel (Arrow keys change option).
@Directive({
  selector: '[appPaginatorKeyCombination]',
})
export class PaginatorKeyCombinationDirective implements OnDestroy {
  private readonly onWindowKeyDownCapture = (event: Event): void => {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }
    this.handlePageSizeChordCapture(event);
  };

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone
  ) {
    addWindowKeydownCaptureListener(this.onWindowKeyDownCapture);
  }

  ngOnDestroy(): void {
    removeWindowKeydownCaptureListener(this.onWindowKeyDownCapture);
  }

  private handlePageSizeChordCapture(event: KeyboardEvent): void {
    if (event.repeat) {
      return;
    }
    if (isActiveElementInsideCdkOverlay()) {
      return;
    }
    const isPaginatorPageSizeChord =
      event.ctrlKey &&
      event.altKey &&
      !event.shiftKey &&
      !event.metaKey;
    if (!isPaginatorPageSizeChord) {
      return;
    }
    const isP =
      (event.key.length === 1 && event.key.toLowerCase() === 'p') ||
      event.code === KEYBOARD_EVENT_CODES.KeyP;
    if (!isP) {
      return;
    }
    const paginator = this.el.nativeElement;
    if (paginator.tagName.toLowerCase() !== 'mat-paginator') {
      return;
    }
    const pageSizeSelect = paginator.querySelector<HTMLElement>('mat-select');
    if (!pageSizeSelect) {
      return;
    }
    if (pageSizeSelect.getAttribute('disabled') !== null) {
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.ngZone.run(() => {
      pageSizeSelect.focus();
      const trigger = pageSizeSelect.querySelector<HTMLElement>(
        '.mat-mdc-select-trigger'
      );
      if (trigger) {
        trigger.dispatchEvent(
          new MouseEvent('click', { bubbles: true, cancelable: true, view: globalThis as unknown as Window })
        );
      }
    });
  }
}

// Paginator: Ctrl+Arrow (no Shift). Handled by `PaginatorArrowWindowRegistryService` (`window`
// capture registered at APP_INITIALIZER so listeners run before Material's mat-select).
@Directive({
  selector: '[appPaginatorNavigation]',
})
export class PaginatorNavigationDirective implements OnDestroy {
  private unregister?: () => void;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly matPaginator: MatPaginator,
    private readonly paginatorArrowRegistry: PaginatorArrowWindowRegistryService
  ) {
    this.unregister = this.paginatorArrowRegistry.register(
      this.el.nativeElement,
      this.matPaginator,
      this.renderer
    );
  }

  ngOnDestroy(): void {
    this.unregister?.();
  }
}

/** Shift+Alt+letter → `app-svg-icon` wrapper class under `appIconClickShortcut` host. */
const ICON_CLASS_BY_LETTER: Readonly<Record<string, string>> = {
  a: 'addIcon',
  v: 'viewIcon',
  u: 'saveIcon',
  e: 'editIcon',
  d: 'deleteIcon',
  s: 'downloadFileIcon',
  p: 'printIcon',
  f: 'filterIcon',
  r: 'refreshIcon',
  t: 'view-more-icon',
  c: 'deleteChildIcon',
};

/** When `event.key` is not a single Latin letter (IME / layout), use physical key. */
const ICON_CLASS_BY_CODE: Readonly<Record<string, string>> = {
  KeyA: 'addIcon',
  KeyV: 'viewIcon',
  KeyU: 'saveIcon',
  KeyE: 'editIcon',
  KeyD: 'deleteIcon',
  KeyS: 'downloadFileIcon',
  KeyP: 'printIcon',
  KeyF: 'filterIcon',
  KeyR: 'refreshIcon',
  KeyT: 'view-more-icon',
  KeyC: 'deleteChildIcon',
};

@Directive({
  selector: '[appIconClickShortcut]',
})
export class IconClickShortcutDirective implements OnDestroy {
  private readonly host: HTMLElement;
  private readonly onDocumentKeyDown = (event: Event): void => {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }
    this.handleKeyboardEvent(event);
  };

  constructor(el: ElementRef<HTMLElement>) {
    this.host = mountHostElementWithDocumentCaptureKeydown(
      el,
      this.onDocumentKeyDown,
      'shift-alt-svg-icon-action'
    );
  }

  ngOnDestroy(): void {
    removeDocumentKeydownCaptureListener(this.onDocumentKeyDown);
  }

  private handleKeyboardEvent(event: KeyboardEvent): void {
    if (!event.shiftKey || !event.altKey || event.metaKey) {
      return;
    }
    const iconClass = this.resolveIconClass(event);
    if (!iconClass) {
      return;
    }
    // Chrome reserves many Shift+Alt+letter chords (tab groups, etc.); plain Shift+Alt+p
    // can open browser UI or a new window. Require Ctrl for print only.
    if (iconClass === 'printIcon') {
      if (!event.ctrlKey) {
        return;
      }
    } else if (event.ctrlKey) {
      return;
    }
    event.preventDefault();
    const iconElement = this.findClickableIcon(iconClass);
    if (!iconElement) {
      return;
    }
    if (
      (globalThis as unknown as Window).getComputedStyle(iconElement).getPropertyValue(
        'pointer-events'
      ) ===
      'none'
    ) {
      return;
    }
    iconElement.dispatchEvent(
      new MouseEvent('click', { bubbles: true, cancelable: true, view: globalThis as unknown as Window })
    );
  }

  private findClickableIcon(iconClass: string): HTMLElement | null {
    const escaped =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(iconClass)
        : iconClass;
    const wrapper = this.host.querySelector<HTMLElement>(
      `div.svg-icon.${escaped}`
    );
    if (wrapper) {
      return wrapper;
    }
    return this.host.querySelector<HTMLElement>(`.${escaped}`);
  }

  private resolveIconClass(event: KeyboardEvent): string | null {
    const letter =
      event.key.length === 1 ? event.key.toLowerCase() : '';
    return (
      ICON_CLASS_BY_LETTER[letter] ?? ICON_CLASS_BY_CODE[event.code] ?? null
    );
  }
}

/**
 * While true, `SortShortcutDirective` must not handle Alt+digit (avoids conflict with Alt+/ then digits).
 * Set by `FocusOnShortcutDirective` on Alt+/; cleared after digit sequence or timeout.
 */
let focusShortcutAltSlashArmed = false;
let focusShortcutArmClearTimer: ReturnType<typeof setTimeout> | undefined;

function armFocusShortcutAltSlashSequence(): void {
  focusShortcutAltSlashArmed = true;
  if (focusShortcutArmClearTimer) {
    clearTimeout(focusShortcutArmClearTimer);
  }
  focusShortcutArmClearTimer = setTimeout(() => {
    focusShortcutAltSlashArmed = false;
    focusShortcutArmClearTimer = undefined;
  }, 5000);
}

function disarmFocusShortcutAltSlashSequence(): void {
  focusShortcutAltSlashArmed = false;
  if (focusShortcutArmClearTimer) {
    clearTimeout(focusShortcutArmClearTimer);
    focusShortcutArmClearTimer = undefined;
  }
}

function isFocusShortcutAltSlashSequenceArmed(): boolean {
  return focusShortcutAltSlashArmed;
}

// making focus on particular input field
@Directive({
  selector: '[appFocusOnShortcut]',
})
export class FocusOnShortcutDirective implements OnDestroy {
  private isEnteringNumber = false;
  private targetNumber: number | null = null;
  private timeoutId: ReturnType<typeof setTimeout> | undefined;
  private escapeFocusTimeoutId?: ReturnType<typeof setTimeout>;

  /** Capture runs before inner `(keydown)="stopPropagation()"` on icons / datepickers. */
  private readonly onDocumentEscapeCapture = (event: Event): void => {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }
    if (
      event.key !== 'Escape' ||
      event.repeat ||
      isActiveElementInsideCdkOverlay()
    ) {
      return;
    }
    const host = this.el.nativeElement as HTMLElement;
    if (!host.isConnected) {
      return;
    }
    const target = resolveFirstShortcutRegionField(host);
    if (!target) {
      return;
    }
    // Do not call `preventDefault` here — some environments treat document keydown as passive,
    // which logs "Unable to preventDefault inside passive event listener invocation".
    // Capture-phase `stopPropagation` is sufficient to preempt inner `(keydown)="stopPropagation()"` handlers.
    event.stopPropagation();
    if (this.escapeFocusTimeoutId !== undefined) {
      clearTimeout(this.escapeFocusTimeoutId);
    }
    this.escapeFocusTimeoutId = setTimeout(() => {
      this.escapeFocusTimeoutId = undefined;
      if (!host.isConnected || !target.isConnected) {
        return;
      }
      target.focus({ preventScroll: true });
    }, 0);
  };

  constructor(
    private readonly el: ElementRef,
    private readonly renderer: Renderer2
  ) {
    addDocumentKeydownCaptureListener(this.onDocumentEscapeCapture);
  }

  ngOnDestroy(): void {
    removeDocumentKeydownCaptureListener(this.onDocumentEscapeCapture);
    if (this.escapeFocusTimeoutId !== undefined) {
      clearTimeout(this.escapeFocusTimeoutId);
      this.escapeFocusTimeoutId = undefined;
    }
    if (this.timeoutId !== undefined) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: Event): void {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }
    if (event.altKey && event.key === '/') {
      armFocusShortcutAltSlashSequence();
      this.isEnteringNumber = true;
      this.targetNumber = null;
    } else if (this.isEnteringNumber && !isNaN(parseInt(event.key, 10))) {
      if (this.timeoutId !== undefined) {
        clearTimeout(this.timeoutId);
      }
      this.targetNumber = this.targetNumber
        ? this.targetNumber * 10 + parseInt(event.key, 10)
        : parseInt(event.key, 10);
      this.timeoutId = setTimeout(() => {
        this.timeoutId = undefined;
        this.handleSequenceEnd();
      }, 200);
    }
  }

  private handleSequenceEnd() {
    this.isEnteringNumber = false;
    const targetInput = this.el.nativeElement.querySelector(
      `[data-shortcut-number="${this.targetNumber}"]`
    );
    if (targetInput) {
      this.renderer.selectRootElement(targetInput).focus();
    }
    disarmFocusShortcutAltSlashSequence();
  }
}

// Plain Alt+letter: row action icons under host; mat-tab labels; or scroll to `.table-tab-name` (Shipment/Bag/Manifest style sections).
@Directive({
  selector: '[appTabIconClickShortcut]',
})
export class TabShortcutDirective implements OnDestroy {
  private readonly host: HTMLElement;
  private readonly onDocumentKeyDown = (event: Event): void => {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }
    this.handleKeyDown(event);
  };

  constructor(el: ElementRef<HTMLElement>) {
    this.host = mountHostElementWithDocumentCaptureKeydown(
      el,
      this.onDocumentKeyDown,
      'plain-alt-tab-or-section'
    );
  }

  ngOnDestroy(): void {
    removeDocumentKeydownCaptureListener(this.onDocumentKeyDown);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.repeat) {
      return;
    }
    const plainAlt =
      event.altKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.metaKey;
    if (!plainAlt) {
      return;
    }

    let handled = false;
    switch (event.code) {
      case 'KeyI':
        handled = this.triggerIconClick('viewIcon');
        break;
      case 'KeyD':
        handled = this.triggerIconClick('deleteIcon');
        break;
      case 'KeyX':
        handled = this.triggerIconClick('editIcon');
        break;
      case 'KeyA':
        handled = this.triggerIconClick('addIcon');
        break;
      case 'KeyZ':
        handled = this.triggerIconClick('addComIcon');
        break;
      case 'KeyC':
        handled = this.openMatTabOrScrollToSection(['Piece', 'Pieces']);
        break;
      case 'KeyY':
        handled = this.openMatTabOrScrollToSection([
          'Commodity',
          'Shipment',
          'Bags',
          'Pallets',
          'Manifest',
          'Flights',
        ]);
        break;
      case 'KeyB':
        handled = this.openMatTabOrScrollToSection(['Criteria']);
        break;
      case 'KeyG':
        handled = this.activateMatTabStrip('Consignee');
        break;
      case 'KeyW':
        handled = this.activateMatTabStrip('True Shipper');
        break;
      default:
        break;
    }
    if (handled) {
      event.preventDefault();
    }
  }

  private getShortcutQueryScope(): HTMLElement {
    return this.host.closest<HTMLElement>('mat-card') ?? this.host;
  }

  private scrollElementWithinShortcutScope(
    element: HTMLElement,
    scope: HTMLElement
  ): void {
    const innerScroller = this.findInnerVerticalScrollContainer(element, scope);
    if (innerScroller) {
      const elRect = element.getBoundingClientRect();
      const parRect = innerScroller.getBoundingClientRect();
      const padding = 8;
      const nextTop =
        innerScroller.scrollTop + (elRect.top - parRect.top) - padding;
      const maxTop = Math.max(
        0,
        innerScroller.scrollHeight - innerScroller.clientHeight
      );
      innerScroller.scrollTo({
        top: Math.min(Math.max(0, nextTop), maxTop),
        behavior: 'smooth',
      });
      return;
    }
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }

  private findInnerVerticalScrollContainer(
    element: HTMLElement,
    boundary: HTMLElement
  ): HTMLElement | null {
    let parent: HTMLElement | null = element.parentElement;
    while (parent && boundary.contains(parent) && parent !== boundary) {
      if (
        this.isVerticallyScrollable(parent) &&
        !parent.querySelector('app-breadcrumb')
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  private isVerticallyScrollable(el: HTMLElement): boolean {
    const overflowY = getComputedStyle(el).overflowY;
    if (
      overflowY !== 'auto' &&
      overflowY !== 'scroll' &&
      overflowY !== 'overlay'
    ) {
      return false;
    }
    return el.scrollHeight > el.clientHeight;
  }

  private triggerIconClick(iconClass: string): boolean {
    const escaped =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(iconClass)
        : iconClass;
    const primarySel = `div.svg-icon.${escaped}`;
    const altSel = `.${escaped}`;

    const dispatchIfClickable = (node: HTMLElement | null): boolean => {
      if (!node || node.classList.contains('disabled')) {
        return false;
      }
      if (
        (globalThis as unknown as Window).getComputedStyle(node).getPropertyValue(
          'pointer-events'
        ) === 'none'
      ) {
        return false;
      }
      node.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: globalThis as unknown as Window,
        })
      );
      return true;
    };

    const active = document.activeElement;
    if (active && this.host.contains(active)) {
      const tabBlock = active.closest<HTMLElement>('.table-tabs-container');
      if (tabBlock && this.host.contains(tabBlock)) {
        const inBlock =
          tabBlock.querySelector<HTMLElement>(primarySel) ??
          tabBlock.querySelector<HTMLElement>(altSel);
        if (dispatchIfClickable(inBlock)) {
          return true;
        }
      }
    }

    let el = this.host.querySelector<HTMLElement>(primarySel);
    el ??= this.host.querySelector<HTMLElement>(altSel);
    return dispatchIfClickable(el);
  }

  /** Try each mat-tab label, then scroll to a summary `.table-tab-name` (e.g. Pieces vs Piece). */
  private openMatTabOrScrollToSection(labels: string[]): boolean {
    const scope = this.getShortcutQueryScope();
    for (const label of labels) {
      if (this.activateMatTabStrip(label, scope)) {
        return true;
      }
    }
    return this.scrollTableTabNameIntoView(labels, scope);
  }

  /** @returns true if a Material tab was activated */
  private activateMatTabStrip(tabLabel: string, scope?: HTMLElement): boolean {
    const root = scope ?? this.getShortcutQueryScope();
    let activated = false;
    const tabLabels = root.querySelectorAll(
      '.mdc-tab__text-label'
    ) as NodeListOf<HTMLElement>;
    tabLabels.forEach(labelElement => {
      if (activated) {
        return;
      }
      if (labelElement.textContent?.trim() !== tabLabel) {
        return;
      }
      const tabElement = labelElement.closest('.mdc-tab') as HTMLElement | null;
      if (!tabElement) {
        return;
      }
      activated = true;
      const strip = tabElement.closest('.mat-mdc-tab-list, .mat-tab-list');
      const allTabs = strip
        ? strip.querySelectorAll('.mdc-tab')
        : root.querySelectorAll('.mdc-tab');
      allTabs.forEach(tab =>
        tab.classList.remove('mdc-tab-indicator--active')
      );
      tabElement.classList.add('mdc-tab-indicator--active');
      tabElement.focus();
      tabElement.click();
      this.scrollElementWithinShortcutScope(tabElement, root);
    });
    return activated;
  }

  private scrollTableTabNameIntoView(
    labels: string[],
    scope?: HTMLElement
  ): boolean {
    const root = scope ?? this.getShortcutQueryScope();
    const candidates = root.querySelectorAll(
      '.table-tab-name'
    ) as NodeListOf<HTMLElement>;
    for (const el of Array.from(candidates)) {
      const text = el.textContent?.trim() ?? '';
      const match = labels.some(
        l => l === text || (l === 'Piece' && text === 'Pieces')
      );
      if (match) {
        const scrollTarget =
          el.closest<HTMLElement>('.table-tabs-container') ??
          el.closest<HTMLElement>('.table-tabs-header') ??
          el;
        this.scrollElementWithinShortcutScope(scrollTarget, root);
        requestAnimationFrame(() => focusFirstFocusableIn(scrollTarget));
        return true;
      }
    }
    return false;
  }
}

/** Clicks the grid Add icon (`addIcon`) within a table-tabs shortcut host. */
function dispatchTableTabAddIconClick(host: HTMLElement): boolean {
  const iconClass = 'addIcon';
  const escaped =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(iconClass)
      : iconClass;
  const primarySel = `div.svg-icon.${escaped}`;
  const altSel = `.${iconClass}`;

  const dispatchIfClickable = (node: HTMLElement | null): boolean => {
    if (!node || node.classList.contains('disabled')) {
      return false;
    }
    if (
      (globalThis as unknown as Window).getComputedStyle(node).getPropertyValue(
        'pointer-events'
      ) === 'none'
    ) {
      return false;
    }
    node.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: globalThis as unknown as Window,
      })
    );
    return true;
  };

  const active = document.activeElement;
  if (active instanceof HTMLElement && host.contains(active)) {
    const tabBlock = active.closest<HTMLElement>('.table-tabs-container');
    if (tabBlock && host.contains(tabBlock)) {
      const inBlock =
        tabBlock.querySelector<HTMLElement>(primarySel) ??
        tabBlock.querySelector<HTMLElement>(altSel);
      if (dispatchIfClickable(inBlock)) {
        return true;
      }
    }
  }

  let el = host.querySelector<HTMLElement>(primarySel);
  el ??= host.querySelector<HTMLElement>(altSel);
  return dispatchIfClickable(el);
}

/**
 * AP/AR charge grids only — **Shift+Alt+N** (New row). Shipment header and other
 * shortcuts (e.g. Shift+Alt+A, Alt+A on commodity) are unchanged.
 */
@Directive({
  selector: '[appApArChargeGridAddShortcut]',
})
export class ApArChargeGridAddShortcutDirective implements OnDestroy {
  private readonly host: HTMLElement;
  private readonly onDocumentKeyDown = (event: Event): void => {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }
    this.handleKeyDown(event);
  };

  constructor(el: ElementRef<HTMLElement>) {
    this.host = mountHostElementWithDocumentCaptureKeydown(
      el,
      this.onDocumentKeyDown,
      'shift-alt-n-ap-ar-charge-add'
    );
  }

  ngOnDestroy(): void {
    removeDocumentKeydownCaptureListener(this.onDocumentKeyDown);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.repeat) {
      return;
    }
    const isShiftAltN =
      event.code === 'KeyN' &&
      event.altKey &&
      event.shiftKey &&
      !event.ctrlKey &&
      !event.metaKey;
    if (!isShiftAltN) {
      return;
    }
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.host.contains(active)) {
      return;
    }
    if (dispatchTableTabAddIconClick(this.host)) {
      event.preventDefault();
    }
  }
}

// Accordion: Ctrl+Left/Right collapse/expand; Alt+letter jumps to a panel tab (see [tabShortcuts] + data-keystroke-tab).
@Directive({
  selector: '[accordionExpandCollapse]',
})
export class AccordionExpandCollapseDirective {
  @Input() tabShortcuts: Record<string, string> | null = null;

  constructor(
    private readonly panel: MatExpansionPanel,
    private readonly host: ElementRef<HTMLElement>
  ) {}

  @HostListener('window:keydown', ['$event'])
  handleWindowKeydown(event: KeyboardEvent): void {
    if (event.repeat) {
      return;
    }
    if (event.ctrlKey && !event.altKey && !event.metaKey) {
      this.handleCtrlArrow(event);
      return;
    }
    if (
      event.altKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey &&
      this.tabShortcuts
    ) {
      this.handleAltLetterTab(event);
    }
  }

  private handleCtrlArrow(event: KeyboardEvent): void {
    const key = event.key;
    if (key !== 'ArrowRight' && key !== 'ArrowLeft') {
      return;
    }
    event.preventDefault();
    if (key === 'ArrowRight') {
      this.panel.open();
      this.focusFirstFocusableInPanel();
      return;
    }
    this.panel.close();
  }

  private handleAltLetterTab(event: KeyboardEvent): void {
    const map = this.tabShortcuts;
    if (!map) {
      return;
    }
    const letter =
      event.key.length === 1 ? event.key.toLowerCase() : '';
    const tabTitle = letter ? map[letter] : '';
    if (!tabTitle) {
      return;
    }
    event.preventDefault();
    this.activateTabByCanonicalTitle(tabTitle);
  }

  private activateTabByCanonicalTitle(canonicalTitle: string): void {
    const root = this.host.nativeElement;
    const withAttr = root.querySelectorAll<HTMLElement>(
      'mat-panel-title[data-keystroke-tab]'
    );
    for (const el of Array.from(withAttr)) {
      if (el.dataset['keystrokeTab'] === canonicalTitle) {
        this.clickPanelTitle(el);
        return;
      }
    }
    this.activateTabByVisibleText(canonicalTitle);
  }

  private activateTabByVisibleText(canonicalTitle: string): void {
    const titles = this.host.nativeElement.querySelectorAll('mat-panel-title');
    for (const node of Array.from(titles)) {
      const el = node as HTMLElement;
      const text = el.textContent?.trim() ?? '';
      const exact = text === canonicalTitle;
      const notesTab =
        canonicalTitle === 'Notes' && text.startsWith('Notes');
      if (exact || notesTab) {
        this.clickPanelTitle(el);
        return;
      }
    }
  }

  private clickPanelTitle(el: HTMLElement): void {
    el.dispatchEvent(
      new MouseEvent('click', { bubbles: true, cancelable: true, view: globalThis as unknown as Window })
    );
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  private focusFirstFocusableInPanel(): void {
    const el = this.host.nativeElement.querySelector<HTMLElement>(
      '[tabindex="0"]'
    );
    el?.focus();
  }
}

// Notes (and similar): Ctrl+Shift+B opens "see more" / host handler.
@Directive({
  selector: '[appOpenPopupShortcut]',
})
export class OpenPopupShortcutDirective implements OnDestroy {
  @Output() publicShortcut = new EventEmitter<void>();

  private readonly host = inject(ElementRef<HTMLElement>);

  private readonly onDocumentKeyDown = (event: Event): void => {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }
    this.handlePublicPopup(event);
  };

  constructor() {
    addDocumentKeydownCaptureListener(this.onDocumentKeyDown);
  }

  ngOnDestroy(): void {
    removeDocumentKeydownCaptureListener(this.onDocumentKeyDown);
  }

  private handlePublicPopup(event: KeyboardEvent): void {
    if (event.repeat) {
      return;
    }
    const isNotesChord =
      event.ctrlKey &&
      event.shiftKey &&
      !event.altKey &&
      !event.metaKey;
    if (!isNotesChord) {
      return;
    }
    const isB =
      (event.key.length === 1 && event.key.toLowerCase() === 'b') ||
      event.code === KEYBOARD_EVENT_CODES.KeyB;
    if (!isB) {
      return;
    }
    if (!this.host.nativeElement.isConnected) {
      return;
    }
    event.preventDefault();
    this.publicShortcut.emit();
  }
}

// Open details tab
@Directive({
  selector: '[appOpenDetailsShortcut]',
})
export class OpenDetailsShortcutDirective {
  private readonly keysPressed: string[] = [];

  constructor() {}

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (!event.repeat) {
      const key = event.key.toLowerCase();
      if (!this.keysPressed.includes(key)) {
        this.keysPressed.push(key);
        if (this.keysPressed.length === 4 && this.checkKeyCombination()) {
          this.openDetails();
        }
      }
    }
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent) {
    const key = event.key.toLowerCase();
    const index = this.keysPressed.indexOf(key);
    if (index !== -1) {
      this.keysPressed.splice(index, 1);
    }
  }

  checkKeyCombination(): boolean {
    return (
      this.keysPressed.includes('control') &&
      this.keysPressed.includes('alt') &&
      this.keysPressed.includes('z') &&
      !isNaN(parseInt(this.keysPressed[this.keysPressed.length - 1]))
    );
  }

  openDetails() {
    const rowNumber =
      parseInt(this.keysPressed[this.keysPressed.length - 1]) - 1;
    const anchorTags = document.querySelectorAll('.detail');
    if (rowNumber >= 0 && rowNumber < anchorTags.length) {
      const thirdRowAnchor = anchorTags[rowNumber] as HTMLElement;
      thirdRowAnchor.click();
    } else {
    }
  }
}

// Row hyperlink: Ctrl+Alt+digits (debounced) — opens primary link in that row (1-based).
@Directive({
  selector: '[appHyperLinkShortcut]',
})
export class HawbShortcutDirective implements OnDestroy {
  /** Default matches summary grid primary link styling. */
  @Input() hyperlinkSelector = '.text-hyperlink';

  private static readonly HYPERLINK_ROW_DEBOUNCE_MS = 175;

  private rowDigits: string[] = [];
  private delayTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly el: ElementRef<HTMLElement>) {}

  ngOnDestroy(): void {
    if (this.delayTimer !== undefined) {
      clearTimeout(this.delayTimer);
      this.delayTimer = undefined;
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.repeat) {
      return;
    }
    const isHyperlinkChord =
      event.ctrlKey &&
      event.altKey &&
      !event.shiftKey &&
      !event.metaKey;
    if (!isHyperlinkChord) {
      return;
    }
    const digitChar = parseDigitCharForHyperlink(event);
    if (digitChar === null) {
      return;
    }
    event.preventDefault();
    this.rowDigits.push(digitChar);
    if (this.delayTimer !== undefined) {
      clearTimeout(this.delayTimer);
    }
    this.delayTimer = setTimeout(() => {
      this.delayTimer = undefined;
      this.handleRowSelection();
    }, HawbShortcutDirective.HYPERLINK_ROW_DEBOUNCE_MS);
  }

  private handleRowSelection(): void {
    const rowIndex = Number.parseInt(this.rowDigits.join(''), 10);
    this.rowDigits = [];
    if (rowIndex < 1) {
      return;
    }
    const rows = querySummaryTableDataRows(this.el.nativeElement);
    const row = rows[rowIndex - 1];
    if (!row) {
      return;
    }
    const target = row.querySelector<HTMLElement>(this.hyperlinkSelector);
    target?.click();
  }
}

// makes sort icons to work
@Directive({
  selector: '[appSortIconFocus]',
})
export class SortShortcutDirective {
  @Input() columnIndex!: number;
  private altSlashPressed = false; // Flag to track Alt + '/' key

  constructor(private readonly el: ElementRef) {}

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.columnIndex) {
      return;
    }
    const keyPressed = event.key;
    const key = Number.parseInt(keyPressed, 10);
    if (isFocusShortcutAltSlashSequenceArmed()) {
      return;
    }
    // Check if Alt key is pressed and the key pressed is a number
    if (
      event.altKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.metaKey &&
      !event.repeat &&
      !isNaN(key) &&
      !this.altSlashPressed // Check if Alt + '/' key was not pressed
    ) {
      if (key === this.columnIndex) {
        this.el.nativeElement.click();
        this.el.nativeElement.focus();
      }
    }
    // Check if '/' key is pressed after Alt key
    if (event.altKey && keyPressed === '/') {
      this.altSlashPressed = true;
    } else {
      this.altSlashPressed = false;
    }
  }
}

// Table tab icons
@Directive({
  selector: '[appNoTabIconClickShortcut]',
})
export class TabNoOnlyShortcutDirective {
  constructor(private readonly el: ElementRef) {}

  @HostListener('document:keydown.alt.i', ['$event'])
  handleAltIShortcut() {
    this.triggerIconClick('viewIcon');
  }

  @HostListener('document:keydown.alt.d', ['$event'])
  handleAltDShortcut() {
    this.triggerIconClick('deleteIcon');
  }

  @HostListener('document:keydown.alt.x', ['$event'])
  handleAltXShortcut() {
    this.triggerIconClick('editIcon');
  }

  private triggerIconClick(iconClass: string) {
    const iconElement = this.el.nativeElement.querySelector(`.${iconClass}`);
    if (iconElement && !iconElement.classList.contains('disabled')) {
      const clickEvent = new MouseEvent('click', { bubbles: true });
      iconElement.dispatchEvent(clickEvent);
    }
  }
}

/**
 * Directive that defines a section for the `appSectionChangeShortcutDirective`.
 *
 * @param {boolean} isActive - Indicates whether the section is currently active.
 * @param {number} sectionNumber - Specifies the section number, allowing the user to jump between sections.
 *
 * @directive
 */
@Directive({
  selector: '[appSection]',
  standalone: true,
})
export class SectionDirective implements OnInit, OnDestroy {
  public elementRef: ElementRef = inject(ElementRef);
  public parentShortCutDirective = inject(SectionChangeShortcutDirective, {
    optional: true,
  });
  /** Indicates whether the section is currently active. */
  @Input({ transform: booleanAttribute }) isActive = false;
  /** Specifies the section number, allowing the user to jump between sections. */
  @Input({ transform: numberAttribute, alias: 'appSection', required: true })
  sectionNumber!: number;
  /** Prevent click event to be triggered
   * and only emit a directive-specific event so that it does not interfere with DOM events */
  @Output() sectionChange: EventEmitter<undefined> =
    new EventEmitter<undefined>();

  ngOnInit() {
    if (!this.parentShortCutDirective && !environment.production) {
      console.warn(
        `This directive needs to be used with 'SectionChangeShortcutDirective'`
      );
    }

    if (this.parentShortCutDirective) {
      this.parentShortCutDirective.addDirective(this);
    }
  }

  ngOnDestroy() {
    if (this.parentShortCutDirective) {
      this.parentShortCutDirective.removeSection(this);
    }
  }
}

/**
 * Directive that facilitates keyboard navigation for jumping between sections.
 * With `CTRL + ALT + F(<section number>)` Or `CTRL + ALT + <ArrowLeft/ArrowRight>`
 *                                                Arrow Left would navigate to the previous section
 *                                                Arrow Right would navigate to the next section
 * With `CTRL + ALT + r` user can jump between last and current active section (Cyclic)
 *
 * To define the sections, use the `appSection` (SectionDirective) which should be a
 * child of the current directive.
 * It is recommended to use this directive as a
 * `hostDirective` and define the sections within the HTML template.
 *
 * This directive automatically manages tab switching and focuses on the first input
 * within the selected tab.
 *
 * To enable lazy-loaded tab switching, ensure that the elements receiving the click
 * event have a `role="tab"` attribute set.
 *
 * Example:
 *
 * ```html
 * @for (title of titles; track title; let idx = $index) {
 *   <mat-panel-title
 *     role="tab"
 *     [appSection]="idx + 2"
 *     (click)="loadComponent(title, panel)">
 *     <span>{{ title }}</span>
 *   </mat-panel-title>
 * }
 * ```
 *
 * Lazy-loaded tabs are placed inside a separate container.
 * Therefore, it is important
 * to properly identify this container.
 * Add an `id="tab-container"` to the container element:
 *
 * ```html
 * <div id="tab-container" #container></div>
 * ```
 *
 * These configurations are enough for the directive to function properly.
 *
 * @example
 * <div appSectionChangeShortcutDirective>
 *   <mat-panel-title role="tab" [appSection]="1" (click)="onTabClick()">Tab 1</mat-panel-title>
 *   <div id="tab-container" #container></div>
 * </div>
 *
 * @directive
 * @hostDirective
 */
@Directive({
  selector: '[appSectionChangeShortcutDirective]',
  standalone: true,
})
export class SectionChangeShortcutDirective implements AfterViewInit {
  private sections: SectionDirective[] = [];
  public elementRef: ElementRef = inject(ElementRef);
  private readonly changeDetectorRef: ChangeDetectorRef =
    inject(ChangeDetectorRef);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private lastSection?: SectionDirective;

  addDirective(section: SectionDirective): void {
    this.sections.push(section);
    this.sections = this.sections.toSorted(
      (a, b) => a.sectionNumber - b.sectionNumber
    );
  }

  removeSection(section: SectionDirective): void {
    this.sections = this.sections.filter(s => s !== section);
    this.sections = this.sections.toSorted(
      (a, b) => a.sectionNumber - b.sectionNumber
    );
  }

  ngAfterViewInit(): void {
    this.handleGoToPreviousSection();
    this.handleDirectSectionJumps();
    this.handleBackAndForwardSectionSwitch();
    combineLatest(
      this.sections.map(section => {
        return fromEvent(section.elementRef.nativeElement, 'click').pipe(
          tap(() => {
            this.storeHistoryStack();
            this.markSectionAsActive(section);
          })
        );
      })
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private handleBackAndForwardSectionSwitch() {
    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        filter(
          event =>
            event.ctrlKey &&
            event.altKey &&
            (event.key === 'ArrowRight' || event.key === 'ArrowLeft')
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        const activeSection = this.sections.findIndex(
          ({ isActive }) => isActive
        );
        const newActiveIndex =
          event.key === 'ArrowRight' ? activeSection + 1 : activeSection - 1;

        if (newActiveIndex < 0 || newActiveIndex > this.sections.length) {
          return;
        }
        this.storeHistoryStack();
        this.markSectionAsActive(this.sections[newActiveIndex]);
        const element: HTMLElement =
          this.sections[newActiveIndex]?.elementRef.nativeElement;

        this.handleSectionChangeAndFocus(
          this.sections[newActiveIndex],
          element
        );
      });
  }

  handleGoToPreviousSection(): void {
    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        filter(
          (event: KeyboardEvent) =>
            event.ctrlKey && event.altKey && event.key.toLowerCase() === 'r'
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (!this.lastSection) {
          return;
        }
        const lastSection = this.lastSection;

        this.storeHistoryStack();
        this.markSectionAsActive(lastSection);

        this.handleSectionChangeAndFocus(
          lastSection,
          lastSection?.elementRef.nativeElement as HTMLElement
        );
      });
  }

  private handleDirectSectionJumps() {
    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        filter(
          event =>
            event.ctrlKey &&
            event.altKey &&
            event.key.includes('F') &&
            !!Number(event.key.replace('F', ''))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        const number = Number(event.key.replace('F', ''));
        const section = this.sections.find(
          ({ sectionNumber }) => sectionNumber === number
        );

        if (!section) {
          return;
        }

        this.storeHistoryStack();
        this.markSectionAsActive(section);
        const element: HTMLElement = section?.elementRef.nativeElement;

        this.handleSectionChangeAndFocus(section, element);
      });
  }

  private handleSectionChangeAndFocus(
    section: SectionDirective | undefined,
    element: HTMLElement
  ): void {
    if (!element || !section) {
      return;
    }

    const isTab = element.getAttribute('role')?.toLowerCase() === 'tab';

    if (isTab) {
      if (section.sectionChange.observed) {
        section.sectionChange.emit();
      } else {
        element.click();
      }

      this.ngZone.onStable
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          const firstInput: HTMLInputElement = this.elementRef.nativeElement
            .querySelector('#tab-container')
            ?.querySelector('input:not([tabindex="-1"])');

          firstInput?.scrollIntoView({ behavior: 'smooth' });
          firstInput?.focus();
          this.changeDetectorRef.detectChanges();
        });

      return;
    }

    const inputElement = element.querySelector(
      'input:not([tabindex="-1"])'
    ) as HTMLInputElement;
    inputElement?.scrollIntoView({ behavior: 'smooth' });

    inputElement?.focus();
    this.changeDetectorRef.detectChanges();
  }

  private storeHistoryStack(): void {
    this.lastSection = this.sections.find(({ isActive }) => isActive);
  }

  private markSectionAsActive(section: SectionDirective): void {
    this.sections.forEach((s: SectionDirective) => {
      s.isActive = s.sectionNumber === section.sectionNumber;
    });
  }
}
